import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signIn, signOut, confirmSignIn } from "aws-amplify/auth";
import { toast } from "react-toastify";
import AuthLayout from "../../layouts/AuthLayout";
import PasswordCriteria from "../../components/auth/PasswordCriteria";
import { validatePasswordStrength } from "../../utils/password-policy";
import { withTransientAuthSession } from "../../utils/transient-auth-session";
import { reportCognitoFailure } from "../../utils/flow-telemetry";



/**
 * Landing page for the "Set up your account" link in the staff invite email.
 *
 * The link carries the user's email plus a one-time Cognito temporary password
 * (`code`). We sign in with those behind the scenes to reach the
 * NEW_PASSWORD_REQUIRED challenge — the same challenge LoginPage drives — and let
 * the user pick their real password, so they never have to know or type the temp
 * password themselves.
 */
const SetPasswordPage: React.FC = () => {
  const navigate = useNavigate();

  // The invite credentials ride in the URL *fragment* (after '#'), not the query
  // string, so the one-time temp password is never sent to the frontend host and
  // never lands in its access logs (CloudFront/Amplify/nginx). The browser keeps
  // the fragment client-side and strips it from the Referer header. We read it
  // once on mount (useState initialiser runs before the scrub effect below), then
  // wipe it from the address bar so it doesn't linger in browser history either.
  const [{ email, code }] = useState(() => {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    return {
      email: (params.get("email") ?? "").trim(),
      code: params.get("code") ?? "",
    };
  });

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const linkValid = Boolean(email && code);

  useEffect(() => {
    if (code) window.history.replaceState({}, "", "/set-password");
  }, [code]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const strengthError = validatePasswordStrength(newPassword);
    if (strengthError) {
      setError(strengthError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      // confirmSignIn below establishes a real session and fires Amplify's
      // `signedIn`, even though this flow signs straight back out of it. Mark
      // the whole window so no `signedIn` listener treats it as an arrival.
      await withTransientAuthSession(async () => {
        // Clear any stale Amplify session first: signIn() throws
        // UserAlreadyAuthenticatedException when a previous session lingers.
        try { await signOut(); } catch { /* ignore if not signed in */ }

        const { isSignedIn, nextStep } = await signIn({
          username: email,
          password: code,
        });

        if (nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
          await confirmSignIn({ challengeResponse: newPassword });
          // Password set. Mirror the login flow: sign out and send them to the
          // login screen to sign in normally with the password they just chose.
          await signOut().catch(() => {});
          toast.success("Password set. Please sign in with your new password.");
          navigate("/login", { replace: true });
          return;
        }

        // No challenge means the account already has a permanent password: the
        // setup link was already used. Don't leave a half-established session.
        if (isSignedIn) await signOut().catch(() => {});
        toast.info("Your account is already set up. Please sign in.");
        navigate("/login", { replace: true });
      });
    } catch (err: any) {
      reportCognitoFailure("COGNITO_SIGNIN_FAILED", err);
      const message =
        err?.name === "NotAuthorizedException"
          ? "This setup link has expired or was already used. Ask your clinic admin to resend the invite."
          : err?.message || "We could not set your password. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!linkValid) {
    return (
      <AuthLayout
        title="Set up your account"
        subtitle="This setup link is invalid or incomplete."
        heroTitleLine1="Almost there!"
        heroTitleLine2="Set your password to continue"
      >
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
          This setup link is missing information. Please open the most recent invite
          email, or ask your clinic admin to resend it.
        </p>
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="mt-4 text-sm text-[#0066cc] hover:underline"
        >
          Back to login
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set up your account"
      subtitle="Choose a password to finish setting up your account."
      heroTitleLine1="Almost there!"
      heroTitleLine2="Set your password to continue"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-slate-500">
          Setting the password for{" "}
          <span className="font-medium text-slate-700">{email}</span>.
        </p>

        <div>
          <label className="block text-sm font-medium text-slate-600">
            New password
          </label>
          <div className="mt-1 flex items-center rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <input
              type={showNewPassword ? "text" : "password"}
              required
              autoComplete="new-password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
              placeholder="Enter a new password"
            />
            <button type="button" onClick={() => setShowNewPassword(v => !v)} className="ml-2 flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-600" aria-label={showNewPassword ? "Hide password" : "Show password"}>
              {showNewPassword ? <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
            </button>
          </div>
          <PasswordCriteria password={newPassword} />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-600">
            Confirm password
          </label>
          <div className="mt-1 flex items-center rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <input
              type={showConfirmPassword ? "text" : "password"}
              required
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
              placeholder="Re-enter new password"
            />
            <button type="button" onClick={() => setShowConfirmPassword(v => !v)} className="ml-2 flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-600" aria-label={showConfirmPassword ? "Hide password" : "Show password"}>
              {showConfirmPassword ? <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
            </button>
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="mt-1 inline-flex items-center justify-center min-h-[44px] rounded-full bg-[#0066ff] px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[#0051cc] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Saving..." : "Set password"}
        </button>
      </form>
    </AuthLayout>
  );
};

export default SetPasswordPage;
